import {useState, useMemo} from "react";
import {Button, Col, Container, Form, FormControl, Row, Spinner} from "react-bootstrap";
import {toDateString} from "../service/JobService";
import JobCardList from "./JobCardList";
import JobStatusToggleGroup from "./JobStatusToggleGroup";
import TaskCardList from "./TaskCardList";
import {
    CLEANING_BOARD_MODE,
    TASK_BOARD_MODE,
    cleaningBoardConfig,
    taskBoardConfig,
} from "./scheduler/boardConfigs";
import SchedulerBacklogColumn from "./scheduler/SchedulerBacklogColumn";
import SchedulerWorkerLane from "./scheduler/SchedulerWorkerLane";
import TaskModal from "./scheduler/TaskModal";
import TaskStatusTabs from "./scheduler/TaskStatusTabs";
import {sortSchedulerJobs, stringToDate} from "./scheduler/schedulerUtils";
import useSchedulerBoardData from "./scheduler/useSchedulerBoardData";
import useSchedulerResources from "./scheduler/useSchedulerResources";

const classicCleaningBoardConfig = {
    ...cleaningBoardConfig,
    statusStorageKey: "radioValue",
    legacyStatusStorageKey: null,
    workerStorageKey: null,
};

const classicTaskBoardConfig = {
    ...taskBoardConfig,
    workerStorageKey: null,
};

function formatSameDayAssignmentLabel(workerNames = []) {
    if (workerNames.length === 0) {
        return "";
    }

    if (workerNames.length === 1) {
        return `Day job: ${workerNames[0]}`;
    }

    return `Day job: ${workerNames[0]} +${workerNames.length - 1}`;
}

function convertTimeToMinutes(timeValue) {
    const normalizedValue = String(timeValue || "").trim();
    if (!normalizedValue) {
        return null;
    }

    const [hours, minutes] = normalizedValue.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return null;
    }

    return (hours * 60) + minutes;
}

function getEarlierTime(leftTime, rightTime) {
    if (!leftTime) {
        return rightTime || "";
    }
    if (!rightTime) {
        return leftTime;
    }

    return convertTimeToMinutes(leftTime) <= convertTimeToMinutes(rightTime) ? leftTime : rightTime;
}

function getLaterTime(leftTime, rightTime) {
    if (!leftTime) {
        return rightTime || "";
    }
    if (!rightTime) {
        return leftTime;
    }

    return convertTimeToMinutes(leftTime) >= convertTimeToMinutes(rightTime) ? leftTime : rightTime;
}

export default function Scheduler() {
    const now = new Date();
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(() => {
        const schedulerDate = localStorage.getItem("schedulerDate");
        return schedulerDate || toDateString(now);
    });
    const [searchText, setSearchText] = useState(() => localStorage.getItem("searchText") || "");
    const [activeBoardMode, setActiveBoardMode] = useState(CLEANING_BOARD_MODE);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const schedulerResources = useSchedulerResources();
    const activeFieldWorkerMap = useMemo(() => {
        return schedulerResources.fieldWorkerArray.reduce((workerMap, worker) => {
            if (worker.isDeleted !== true) {
                workerMap[worker.id] = worker.name;
            }
            return workerMap;
        }, {});
    }, [schedulerResources.fieldWorkerArray]);
    const cleaningBoard = useSchedulerBoardData({
        boardConfig: classicCleaningBoardConfig,
        selectedDate,
        searchText,
        fieldWorkerMap: schedulerResources.fieldWorkerMap,
        fieldSupervisorMap: schedulerResources.fieldSupervisorMap,
    });
    const taskBoard = useSchedulerBoardData({
        boardConfig: classicTaskBoardConfig,
        selectedDate,
        searchText,
        fieldWorkerMap: schedulerResources.fieldWorkerMap,
        fieldSupervisorMap: schedulerResources.fieldSupervisorMap,
    });
    const activeBoard = activeBoardMode === TASK_BOARD_MODE ? taskBoard : cleaningBoard;
    const sharedActiveWorkerId = activeBoardMode === TASK_BOARD_MODE
        ? taskBoard.activeWorkerId
        : cleaningBoard.activeWorkerId;
    const mixedWorkerJobs = useMemo(() => {
        return sortSchedulerJobs([
            ...cleaningBoard.workerJobs.map((job) => ({...job, __schedulerCardType: "cleaning"})),
            ...taskBoard.workerJobs.map((job) => ({...job, __schedulerCardType: "task"})),
        ]);
    }, [cleaningBoard.workerJobs, taskBoard.workerJobs]);
    const mixedWorkerCounts = useMemo(() => {
        return [cleaningBoard.workerCounts, taskBoard.workerCounts].reduce((combinedCounts, counts) => {
            Object.entries(counts).forEach(([workerId, count]) => {
                combinedCounts[workerId] = (combinedCounts[workerId] || 0) + count;
            });
            return combinedCounts;
        }, {});
    }, [cleaningBoard.workerCounts, taskBoard.workerCounts]);
    const sameDayTaskAssignments = useMemo(() => {
        return cleaningBoard.jobs.reduce((propertyAssignments, job) => {
            if (job.status !== "Cancelled" && job.property_id != null && job.property_id !== "") {
                const propertyId = String(job.property_id);
                const workerName = schedulerResources.fieldWorkerMap[job.cos_job_worker] || "Unassigned";
                const existingAssignment = propertyAssignments[propertyId] || {
                    workerNames: [],
                    hasSameDayJob: false,
                    checkOut: "",
                    checkIn: "",
                };
                const existingWorkerNames = existingAssignment.workerNames;
                propertyAssignments[propertyId] = {
                    workerNames: existingWorkerNames.includes(workerName)
                        ? existingWorkerNames
                        : [...existingWorkerNames, workerName],
                    hasSameDayJob: true,
                    checkOut: getEarlierTime(existingAssignment.checkOut, job.check_out),
                    checkIn: getLaterTime(existingAssignment.checkIn, job.check_in),
                };
            }
            return propertyAssignments;
        }, {});
    }, [cleaningBoard.jobs, schedulerResources.fieldWorkerMap]);
    const cleaningStatusCountsWithTask = useMemo(() => ({
        ...cleaningBoard.statusCounts,
        Task: (taskBoard.statusCounts.Pending || 0) + (taskBoard.statusCounts.Scheduled || 0),
    }), [cleaningBoard.statusCounts, taskBoard.statusCounts]);
    const boardHeight = Math.max(activeBoard.visibleBacklogJobs.length, mixedWorkerJobs.length, 1) * 420;
    const isLoading = cleaningBoard.isLoading || taskBoard.isLoading;

    function handleDateSelect(nextDate) {
        setSelectedDate(nextDate);
        localStorage.setItem("schedulerDate", nextDate);
    }

    function handleDateOffset(deltaDays) {
        const nextDate = deltaDays === 0
            ? now
            : (() => {
                const currentDate = stringToDate(selectedDate);
                currentDate.setDate(currentDate.getDate() + deltaDays);
                return currentDate;
            })();

        handleDateSelect(toDateString(nextDate));
    }

    function handleSearchTextChange(nextSearchText) {
        setSearchText(nextSearchText);
        localStorage.setItem("searchText", nextSearchText);
    }

    function handleCleaningStatusChange(nextStatus) {
        setActiveBoardMode(CLEANING_BOARD_MODE);
        cleaningBoard.setSelectedStatus(nextStatus);
    }

    function handleTaskModeClick() {
        setActiveBoardMode(TASK_BOARD_MODE);
    }

    function handleTaskStatusChange(nextStatus) {
        setActiveBoardMode(TASK_BOARD_MODE);
        taskBoard.setSelectedStatus(nextStatus);
    }

    function openCreateTaskModal() {
        setEditingTaskId(null);
        setIsTaskModalOpen(true);
    }

    function openEditTaskModal(taskId) {
        setEditingTaskId(taskId);
        setIsTaskModalOpen(true);
    }

    function closeTaskModal() {
        setEditingTaskId(null);
        setIsTaskModalOpen(false);
    }

    function handleBacklogDrop(event) {
        activeBoard.assignWorker(event.dataTransfer.getData("cos_job_id"), activeBoardMode === TASK_BOARD_MODE
            ? classicTaskBoardConfig.defaultWorkerId
            : classicCleaningBoardConfig.defaultWorkerId);
    }

    function handleWorkerDrop(event) {
        activeBoard.assignWorker(event.dataTransfer.getData("cos_job_id"), sharedActiveWorkerId);
    }

    function handleWorkerChange(nextWorkerId) {
        cleaningBoard.setActiveWorkerId(nextWorkerId);
        taskBoard.setActiveWorkerId(nextWorkerId);
    }

    return (
        <Container fluid>
            <Row className="mb-2">
                <Col sm={6} style={{textAlign: "left"}}>
                    <JobStatusToggleGroup
                        radioValue={activeBoardMode === CLEANING_BOARD_MODE ? cleaningBoard.selectedStatus : ""}
                        setRadioValue={handleCleaningStatusChange}
                        statusCounts={cleaningStatusCountsWithTask}
                        isTaskSelected={activeBoardMode === TASK_BOARD_MODE}
                        onTaskClick={handleTaskModeClick}
                    />
                </Col>
                <Col sm={3}>
                    <div style={{display: "flex"}}>
                        <Button
                            disabled={isLoading}
                            variant="outline-secondary"
                            onClick={() => handleDateOffset(-1)}
                        >
                            {"<"}
                        </Button>
                        <Form.Control
                            disabled={isLoading}
                            style={{width: "150px", marginLeft: "10px", marginRight: "10px"}}
                            type="date"
                            name="date"
                            onChange={(event) => handleDateSelect(event.target.value)}
                            value={selectedDate}
                        />
                        <Button
                            disabled={isLoading}
                            variant="outline-secondary"
                            onClick={() => handleDateOffset(1)}
                        >
                            {">"}
                        </Button>
                        <Button
                            style={{marginLeft: "10px"}}
                            disabled={isLoading}
                            variant="outline-secondary"
                            onClick={() => handleDateOffset(0)}
                        >
                            Today
                        </Button>
                    </div>
                </Col>
                <Col sm={1}>
                    <Button
                        style={{width: "80px"}}
                        variant="secondary"
                        disabled={isLoading}
                        onClick={activeBoard.refreshBoard}
                    >
                        {isLoading ? (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                        ) : "Refresh"}
                    </Button>
                </Col>
                <Col sm={2} style={{textAlign: "right"}}>
                    <FormControl
                        disabled={isLoading}
                        value={searchText}
                        onChange={(event) => handleSearchTextChange(event.target.value)}
                        type="search"
                        placeholder="Enter Filter Text..."
                        className="mr-2"
                        aria-label="Search"
                    />
                </Col>
            </Row>
            <hr />
            <Row>
                <SchedulerBacklogColumn
                    boardHeight={boardHeight}
                    hasJobs={activeBoard.visibleBacklogJobs.length > 0}
                    header={activeBoardMode === TASK_BOARD_MODE ? (
                        <div style={{display: "inline-flex", alignItems: "center", gap: "10px", flexWrap: "wrap"}}>
                            <TaskStatusTabs
                                selectedStatus={taskBoard.selectedStatus}
                                statusCounts={taskBoard.statusCounts}
                                onChange={handleTaskStatusChange}
                            />
                            <Button onClick={openCreateTaskModal}>New Task</Button>
                        </div>
                    ) : null}
                    emptyMessage={activeBoardMode === TASK_BOARD_MODE
                        ? classicTaskBoardConfig.emptyMessage
                        : classicCleaningBoardConfig.emptyMessage}
                    onClearFilter={() => handleSearchTextChange("")}
                    onDropJob={handleBacklogDrop}
                >
                    {activeBoard.visibleBacklogJobs.map((job) => (
                        activeBoardMode === TASK_BOARD_MODE ? (
                            <TaskCardList
                                key={job.cos_job_id}
                                job={job}
                                fieldWorkerMap={activeFieldWorkerMap}
                                sameDayAssignmentLabel={formatSameDayAssignmentLabel(
                                    sameDayTaskAssignments[String(job.property_id)]?.workerNames || [],
                                )}
                                sameDaySchedule={sameDayTaskAssignments[String(job.property_id)] || null}
                                isLoading={activeBoard.isLoading}
                                onEditJob={() => openEditTaskModal(job.cos_job_id)}
                            />
                        ) : (
                            <JobCardList
                                key={job.cos_job_id}
                                job={job}
                                fieldSupervisorMap={schedulerResources.fieldSupervisorMap}
                                fieldWorkerMap={activeFieldWorkerMap}
                                isLoading={activeBoard.isLoading}
                                onJobUpdated={activeBoard.updateJobInState}
                            />
                        )
                    ))}
                </SchedulerBacklogColumn>
                <SchedulerWorkerLane
                    activeWorkerId={sharedActiveWorkerId}
                    boardHeight={boardHeight}
                    fieldWorkerArray={schedulerResources.fieldWorkerArray}
                    fieldWorkerMap={schedulerResources.fieldWorkerMap}
                    onActiveWorkerChange={handleWorkerChange}
                    onDropJob={handleWorkerDrop}
                    workerCounts={mixedWorkerCounts}
                >
                    {mixedWorkerJobs.map((job) => (
                        job.__schedulerCardType === "task" ? (
                            <TaskCardList
                                key={job.cos_job_id}
                                job={job}
                                fieldWorkerMap={activeFieldWorkerMap}
                                sameDayAssignmentLabel={formatSameDayAssignmentLabel(
                                    sameDayTaskAssignments[String(job.property_id)]?.workerNames || [],
                                )}
                                sameDaySchedule={sameDayTaskAssignments[String(job.property_id)] || null}
                                isLoading={activeBoard.isLoading}
                                onEditJob={() => openEditTaskModal(job.cos_job_id)}
                            />
                        ) : (
                            <JobCardList
                                key={job.cos_job_id}
                                job={job}
                                fieldSupervisorMap={schedulerResources.fieldSupervisorMap}
                                fieldWorkerMap={activeFieldWorkerMap}
                                isLoading={activeBoard.isLoading}
                                onJobUpdated={activeBoard.updateJobInState}
                            />
                        )
                    ))}
                </SchedulerWorkerLane>
            </Row>
            <TaskModal
                fieldWorkerArray={schedulerResources.fieldWorkerArray}
                initialDate={selectedDate}
                onClose={closeTaskModal}
                onSaved={() => {
                    closeTaskModal();
                    taskBoard.refreshBoard();
                }}
                show={isTaskModalOpen}
                taskId={editingTaskId}
            />
        </Container>
    );
}
